import { toCanonicalAiHost, toCurrentTld } from './workDomain';

declare const __SECSHOT_APP_NAME__: string;
declare const __SECSHOT_HOST__: string;
declare const __SECSHOT_SECRESET_HOST__: string;
declare const __SECSHOT_TAS_DESIGN_HOST__: string;
declare const __SECSHOT_TAS_WILD_HOST__: string;
declare const __SECSHOT_SECCAP_HOST__: string;
declare const __SECSHOT_SECCAP_TEST_HOST__: string;
declare const __SECSHOT_SECCAP_REASON_HOST__: string;
declare const __SECSHOT_SECPOSE_HOST__: string;
declare const __LOCAL_APP_FLOW__: string;

// Strict hostname whitelist. All values are injected at build time from .env.
// Any hostname not in this whitelist will refuse to serve.
// The whitelist is keyed on .maxinsights.ai hosts, but the same bundle is also
// served from the .maxinsights.work mirror (geo-unblocked in China); normalize
// the live host back to its .ai form so both TLDs match the same entry.
const _rawHostname = window.location.hostname;
const _hostname = toCanonicalAiHost(_rawHostname);
const _isLocalDevelopmentHost =
  _rawHostname === 'localhost' ||
  _rawHostname === '127.0.0.1' ||
  _rawHostname === '[::1]' ||
  _rawHostname.endsWith('.localhost');
const TEST_HOST_FLOW: Record<string, string> = {
  'secshot-test.maxinsights.ai': 'secshot',
  'secreset-test.maxinsights.ai': 'secreset',
  'designtas-test.maxinsights.ai': 'tas_design',
  'sectas-test.maxinsights.ai': 'tas_wild',
  'seccap-test.maxinsights.ai': 'seccap',
  'seccapreason-test.maxinsights.ai': 'seccapreason',
  'secpose-test.maxinsights.ai': 'secpose',
};
let _API_FLOW: string;
if (_isLocalDevelopmentHost) {
  _API_FLOW = __LOCAL_APP_FLOW__ || 'secshot';
} else if (TEST_HOST_FLOW[_hostname]) {
  _API_FLOW = TEST_HOST_FLOW[_hostname];
} else if (__SECSHOT_TAS_DESIGN_HOST__ && _hostname === __SECSHOT_TAS_DESIGN_HOST__) {
  _API_FLOW = 'tas_design';
} else if (__SECSHOT_TAS_WILD_HOST__ && _hostname === __SECSHOT_TAS_WILD_HOST__) {
  _API_FLOW = 'tas_wild';
} else if (__SECSHOT_SECRESET_HOST__ && _hostname === __SECSHOT_SECRESET_HOST__) {
  _API_FLOW = 'secreset';
} else if (__SECSHOT_HOST__ && _hostname === __SECSHOT_HOST__) {
  _API_FLOW = 'secshot';
} else if (__SECSHOT_SECCAP_HOST__ && _hostname === __SECSHOT_SECCAP_HOST__) {
  _API_FLOW = 'seccap';
} else if (__SECSHOT_SECCAP_TEST_HOST__ && _hostname === __SECSHOT_SECCAP_TEST_HOST__) {
  _API_FLOW = 'seccap';
} else if (__SECSHOT_SECCAP_REASON_HOST__ && _hostname === __SECSHOT_SECCAP_REASON_HOST__) {
  _API_FLOW = 'seccapreason';
} else if (__SECSHOT_SECPOSE_HOST__ && _hostname === __SECSHOT_SECPOSE_HOST__) {
  _API_FLOW = 'secpose';
} else {
  throw new Error(`Unknown deployment host: ${_hostname}. Not in configured whitelist.`);
}

export const FLOW = _API_FLOW;
export const isTasFlow = _API_FLOW === 'tas_design' || _API_FLOW === 'tas_wild';
export const isResettingFlow = _API_FLOW === 'secreset';
export const DEPLOY_ENV: 'test' | 'dev' | 'prod' = _isLocalDevelopmentHost
  ? 'dev'
  : _hostname.includes('-test.')
    ? 'test'
    : _hostname.includes('-dev.')
      ? 'dev'
      : 'prod';

const AUTH_GROUP_PREFIX: Record<string, string> = {
  secshot: 'secshot',
  secreset: 'secreset',
  tas_design: 'sectas-design',
  tas_wild: 'sectas-wild',
  seccap: 'seccap',
  seccapreason: 'seccapreason',
  secpose: 'secpose',
};
const _groupPrefix = AUTH_GROUP_PREFIX[_API_FLOW] ?? _API_FLOW;

export function getAdminGroupName(): string {
  return `${_groupPrefix}-users-admin`;
}

export function getAuditorGroupName(): string {
  return `${_groupPrefix}-users-audit`;
}

export function getFlowUserGroupPrefix(): string {
  return `${_groupPrefix}-users`;
}

// Per-flow group prefix mapping (mirrors FLOW_USER_GROUP_PREFIX in workers/src/auth.ts)
export const FLOW_GROUP_PREFIXES: Record<string, string> = {
  secshot: 'secshot-users',
  secreset: 'secreset-users',
  tas_design: 'sectas-design-users',
  tas_wild: 'sectas-wild-users',
  seccap: 'seccap-users',
  seccapreason: 'seccapreason-users',
  secpose: 'secpose-users',
};

// Per-flow display names
export const FLOW_DISPLAY_NAMES: Record<string, string> = {
  secshot: 'SecShot',
  secreset: 'SecReset',
  tas_design: 'Design TAS',
  tas_wild: 'Wild TAS',
  seccap: 'SecCap',
  seccapreason: 'SecCapReason',
  secpose: 'Pose Adjust',
};

// Per-flow URLs — build-time injected, prod/test distinction comes from .env
// Cross-flow nav links. Hosts are build-time .ai values; toCurrentTld keeps the
// link on whichever TLD (.ai / .work) the user is currently browsing.
export const FLOW_URLS: Record<string, string> = {
  secshot: __SECSHOT_HOST__ ? toCurrentTld(`https://${__SECSHOT_HOST__}`) : '',
  secreset: __SECSHOT_SECRESET_HOST__ ? toCurrentTld(`https://${__SECSHOT_SECRESET_HOST__}`) : '',
  tas_design: __SECSHOT_TAS_DESIGN_HOST__
    ? toCurrentTld(`https://${__SECSHOT_TAS_DESIGN_HOST__}`)
    : '',
  tas_wild: __SECSHOT_TAS_WILD_HOST__ ? toCurrentTld(`https://${__SECSHOT_TAS_WILD_HOST__}`) : '',
  seccap: __SECSHOT_SECCAP_HOST__
    ? toCurrentTld(`https://${__SECSHOT_SECCAP_HOST__}`)
    : __SECSHOT_SECCAP_TEST_HOST__
      ? toCurrentTld(`https://${__SECSHOT_SECCAP_TEST_HOST__}`)
      : '',
  seccapreason: __SECSHOT_SECCAP_REASON_HOST__
    ? toCurrentTld(`https://${__SECSHOT_SECCAP_REASON_HOST__}`)
    : '',
  secpose: __SECSHOT_SECPOSE_HOST__ ? toCurrentTld(`https://${__SECSHOT_SECPOSE_HOST__}`) : '',
};

// Flow-aware API path builder.
// TAS: /api/tas/..., secshot: /api/...
export function apiPath(path: string): string {
  if (!_API_FLOW) return `/api${path}`;
  return `/api/${_API_FLOW}${path}`;
}

const VIDEO_BASE_URL =
  _API_FLOW === 'tas_design'
    ? 'https://zedpreview.maxinsights.work/max-data-warehouse/zed_data_collection/'
    : _API_FLOW === 'seccap' || _API_FLOW === 'seccapreason'
      ? 'https://cappreview.maxinsights.work/'
      : 'https://egopreview.maxinsights.work/';

const APP_NAME = __SECSHOT_APP_NAME__;

const ADMIN_NAME_ENC = '';
const ADMIN_KEY = 'opaccess';

function decodeAdminName(): string {
  const raw = atob(ADMIN_NAME_ENC);
  let out = '';
  for (let i = 0; i < raw.length; i += 1) {
    const keyChar = ADMIN_KEY.charCodeAt(i % ADMIN_KEY.length);
    out += String.fromCharCode(raw.charCodeAt(i) ^ keyChar);
  }
  return out;
}

const ADMIN_NAME = decodeAdminName();

export function isAdminName(name: string): boolean {
  return !!name && name === ADMIN_NAME;
}

export function getAdminName(): string {
  return ADMIN_NAME;
}

export function applyBranding(): void {
  const name = FLOW_DISPLAY_NAMES[FLOW] ?? APP_NAME;
  if (name) {
    document.title = name;
    document.querySelectorAll<HTMLElement>('[data-brand-name]').forEach((el) => {
      el.textContent = name;
    });
  }
}

// Only tas_wild uses the de-fisheyed .upreview; everyone else uses .preview.
// secshot must use .preview because DDB `duration` is derived from it — its
// .upreview re-encode has a truncated duration, which made encoded bit-arrays
// shorter than the stored duration and failed commits "Encoded payload too short".
const VIDEO_FILENAME_SUFFIX = _API_FLOW === 'tas_wild' ? '.upreview.mp4' : '.preview.mp4';

export function resolveVideoUrl(episodeId: string): string {
  const encoded = encodeURIComponent(episodeId);
  return `${VIDEO_BASE_URL}${encoded}/${encoded}${VIDEO_FILENAME_SUFFIX}`;
}
